import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Alert, AlertDescription } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { 
  Building2, 
  Save, 
  RefreshCw, 
  Plus, 
  Edit, 
  Trash2,
  AlertCircle,
  Users,
  Settings,
  Eye,
  Filter,
  Search,
  Crown,
  Target,
  Cpu,
  BarChart3,
  TrendingUp,
  ChevronDown,
  FolderTree,
  Route,
  FileText,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { getDepartments, createDepartment, updateDepartment } from '../../api/departmentService';
import { getCurrentUser } from '../../api/authService';
import StatusFilterDropdown from '../../components/filters/StatusFilterDropdown';
import RoutingStrategyModal from '../../components/modals/RoutingStrategyModal';
import FallbackDepartmentModal from '../../components/modals/FallbackDepartmentModal';
import AddEditDepartmentModal from '../../components/modals/AddEditDepartmentModal';
import ViewDepartmentModal from '../../components/modals/ViewDepartmentModal';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import toast from 'react-hot-toast';

const DepartmentManagement = () => {
  const [activeTab, setActiveTab] = useState('departments');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [allDepartments, setAllDepartments] = useState([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  
  // Ensure departments is always an array
  const safeDepartments = Array.isArray(departments) ? departments : [];
  const [departmentHierarchy, setDepartmentHierarchy] = useState({});
  const [departmentSettings, setDepartmentSettings] = useState({});
  const [routingRules, setRoutingRules] = useState({});
  const [performanceMetrics, setPerformanceMetrics] = useState({});
  const [message, setMessage] = useState(null);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [showViewDepartmentModal, setShowViewDepartmentModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showRoutingStrategyModal, setShowRoutingStrategyModal] = useState(false);
  const [showFallbackDepartmentModal, setShowFallbackDepartmentModal] = useState(false);
  const [showAddDepartmentModal, setShowAddDepartmentModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [departmentToDelete, setDepartmentToDelete] = useState(null);

  useEffect(() => {
    loadDepartmentData();
  }, []);

  // Reload departments when page or filters change
  useEffect(() => {
    if (activeTab === 'departments') {
      loadDepartmentData();
    }
  }, [currentPage, pageSize, selectedStatus, searchQuery]);

  const loadDepartmentData = async () => {
    setLoading(true);
    try {
      // Prepare query parameters
      const params = {
        page: currentPage,
        size: pageSize
      };
      
      // Add isActive filter if selected
      if (selectedStatus === 'active') {
        params.isActive = true;
      } else if (selectedStatus === 'inactive') {
        params.isActive = false;
      }
      // If selectedStatus is null, don't include isActive - backend returns all
      
      // Load departments from backend API
      const departmentsResponse = await getDepartments(params);
      
      //console.log('📋 Departments API Response:', departmentsResponse);
      
      // Backend returns: { data: { data: { content: [...], totalElements, totalPages, ... } } }
      // Or: { data: { data: [...] } } for direct array
      let departmentsData = [];
      let total = 0;
      let pages = 0;
      
      if (departmentsResponse?.data?.data) {
        const responseData = departmentsResponse.data.data;
        
        // Check if it's paginated response (has content array)
        if (responseData.content && Array.isArray(responseData.content)) {
          departmentsData = responseData.content;
          total = responseData.totalElements || 0;
          pages = responseData.totalPages || 0;
        } 
        // Check if it's direct array
        else if (Array.isArray(responseData)) {
          departmentsData = responseData;
          total = responseData.length;
          pages = Math.ceil(total / pageSize);
        }
      } 
      // Fallback: check if data is directly an array
      else if (Array.isArray(departmentsResponse?.data)) {
        departmentsData = departmentsResponse.data;
        total = departmentsResponse.data.length;
        pages = Math.ceil(total / pageSize);
      }
      
      // Apply client-side search filter if searchQuery is provided
      let filteredData = departmentsData;
      if (searchQuery && searchQuery.trim() !== '') {
        const query = searchQuery.toLowerCase().trim();
        filteredData = departmentsData.filter(dept => {
          const name = (dept.departmentName || dept.name || '').toLowerCase();
          const code = (dept.departmentCode || dept.code || '').toLowerCase();
          const description = (dept.description || '').toLowerCase();
          return name.includes(query) || code.includes(query) || description.includes(query);
        });
        // Update total for filtered results
        total = filteredData.length;
        pages = Math.ceil(total / pageSize);
      }
      
      // Map backend format to frontend format
      const mappedDepartments = filteredData.map(dept => ({
        id: dept.departmentId || dept.id,
        name: dept.departmentName || dept.name,
        code: dept.departmentCode || dept.code,
        description: dept.description || '',
        status: dept.isActive === true ? 'active' : (dept.isActive === false ? 'inactive' : dept.status || 'inactive'),
        isActive: dept.isActive !== undefined ? dept.isActive : (dept.status === 'active'),
        type: dept.type || 'general',
        memberCount: dept.userCount || dept.memberCount || 0,
        ticketCount: dept.ticketCount || 0,
        manager: dept.managerName || dept.manager || null,
        managerId: dept.managerId || null,
        organizationId: dept.organizationId || null,
        organizationName: dept.organizationName || null,
        parentDepartmentId: dept.parentDepartmentId || null,
        parentDepartmentName: dept.parentDepartmentName || null,
        teamCount: dept.teamCount || 0,
        createdAt: dept.createdAt || new Date().toISOString(),
        updatedAt: dept.updatedAt || null
      }));
      
      //console.log('✅ Mapped departments:', mappedDepartments);
      
      setAllDepartments(mappedDepartments);
      setDepartments(mappedDepartments);
      setTotalElements(total);
      setTotalPages(pages);
      
      // Initialize other data with default values
      // These can be implemented later with real APIs
      setDepartmentHierarchy({ enabled: false, levels: [] });
      setDepartmentSettings({ enabled: false });
      setRoutingRules({ enabled: false, rules: [] });
      setPerformanceMetrics({});
      
      // Only show success toast if not loading on mount or filter change
      if (mappedDepartments.length > 0 && currentPage === 0) {
        // toast.success(`Loaded ${mappedDepartments.length} department(s)`);
      }
    } catch (error) {
      console.error('Error loading department data:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load departments';
      setMessage({ type: 'error', text: errorMessage });
      toast.error(errorMessage);
      
      // Set default values on error
      setAllDepartments([]);
      setDepartments([]);
      setTotalElements(0);
      setTotalPages(0);
      setDepartmentHierarchy({ enabled: false, levels: [] });
      setDepartmentSettings({ enabled: false });
      setRoutingRules({ enabled: false, rules: [] });
      setPerformanceMetrics({});
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Simulate saving department settings
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      localStorage.setItem('departments', JSON.stringify(departments));
      localStorage.setItem('departmentHierarchy', JSON.stringify(departmentHierarchy));
      localStorage.setItem('departmentSettings', JSON.stringify(departmentSettings));
      localStorage.setItem('routingRules', JSON.stringify(routingRules));
      
      setMessage({ type: 'success', text: 'Department settings saved successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save department settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleCreateDepartment = () => {
    setShowAddDepartmentModal(true);
    setEditingDepartment(null);
  };

  const handleEditDepartment = (department) => {
    setEditingDepartment(department);
    setShowAddDepartmentModal(true);
  };

  const handleSaveDepartment = async (departmentData) => {
    setSaving(true);
    setMessage(null); // Clear any previous messages
    
    try {
      // Get current user to extract tenant ID
      const currentUser = getCurrentUser();
      
      // Get tenant ID from current user (from JWT token) or sessionStorage
      let tenantId = null;
      if (currentUser?.tenantId) {
        tenantId = currentUser.tenantId;
      } else {
        // Fallback to sessionStorage
        const storedTenantId = sessionStorage.getItem('tenantId');
        if (storedTenantId) {
          tenantId = parseInt(storedTenantId, 10);
        }
      }
      
      if (!tenantId) {
        throw new Error('Tenant ID not found. Please log in again.');
      }
      
      // Prepare department data with tenant ID
      // Note: organizationId removed - departments use tenant only
      const departmentPayload = {
        ...departmentData,
        tenantId: tenantId
      };
      
      let response;
      
      if (editingDepartment) {
        // Update existing department
        const departmentId = editingDepartment.id || editingDepartment.departmentId;
        if (!departmentId) {
          throw new Error('Department ID is required for update');
        }
        
        response = await updateDepartment(departmentId, departmentPayload);
        
        // Extract response data
        const responseData = response?.data?.data || response?.data;
        
        if (response?.data?.success !== false && responseData) {
          toast.success('Department updated successfully');
          // setMessage({ type: 'success', text: 'Department updated successfully' });
          
          // Refresh the department list
          await loadDepartmentData();
        } else {
          throw new Error(response?.data?.message || 'Failed to update department');
        }
      } else {
        // Create new department
        response = await createDepartment(departmentPayload);
        
        // Extract response data
        const responseData = response?.data?.data || response?.data;
        
        if (response?.data?.success !== false && responseData) {
          toast.success('Department created successfully');
          // setMessage({ type: 'success', text: 'Department created successfully' });
          
          // Refresh the department list
          await loadDepartmentData();
        } else {
          throw new Error(response?.data?.message || 'Failed to create department');
        }
      }
      
      // Close modal after successful save
      setShowAddDepartmentModal(false);
      setEditingDepartment(null);
      
    } catch (error) {
      console.error('Error saving department:', error);
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          'Failed to save department. Please try again.';
      setMessage({ type: 'error', text: errorMessage });
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleRoutingStrategySelect = (strategy) => {
    setRoutingRules(prev => ({ ...prev, strategy }));
  };

  const handleFallbackDepartmentSelect = (department) => {
    setRoutingRules(prev => ({ ...prev, fallbackDepartment: department }));
  };

  const handleViewDepartment = (department) => {
    setSelectedDepartment(department);
    setShowViewDepartmentModal(true);
  };

  const handleDeleteDepartment = (departmentId) => {
    const dept = allDepartments.find(d => d.id === departmentId);
    setDepartmentToDelete(dept);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteDepartment = async () => {
    if (!departmentToDelete) return;
    
    try {
      // Simulate department deletion
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Remove department from state
      setAllDepartments(prev => {
        const prevArray = Array.isArray(prev) ? prev : [];
        return prevArray.filter(dept => dept.id !== departmentToDelete.id);
      });
      setMessage({ type: 'success', text: 'Department deleted successfully' });
      setShowDeleteConfirm(false);
      setDepartmentToDelete(null);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete department' });
    }
  };

  const getDepartmentColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-50 text-green-700 border-green-200';
      case 'inactive': return 'bg-gray-50 text-gray-700 border-gray-200';
      case 'maintenance': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getDepartmentIcon = (type) => {
    switch (type) {
      case 'technical': return <Cpu className="h-4 w-4" />;
      case 'customer-service': return <Users className="h-4 w-4" />;
      case 'sales': return <Target className="h-4 w-4" />;
      case 'management': return <Crown className="h-4 w-4" />;
      default: return <Building2 className="h-4 w-4" />;
    }
  };

  // Filter departments based on selected status and search query
  useEffect(() => {
    // Ensure allDepartments is always an array
    const departmentsArray = Array.isArray(allDepartments) ? allDepartments : [];
    let filteredDepartments = [...departmentsArray];

    // Filter by status
    if (selectedStatus) {
      filteredDepartments = filteredDepartments.filter(dept => 
        dept && dept.status && dept.status?.toLowerCase() === selectedStatus.toLowerCase()
      );
    }

    // Filter by search query
    if (searchQuery && searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filteredDepartments = filteredDepartments.filter(dept =>
        dept && (
          dept?.name?.toLowerCase().includes(query) ||
          dept?.description?.toLowerCase().includes(query) ||
          dept?.type?.toLowerCase().includes(query)
        )
      );
    }

    setDepartments(filteredDepartments);
  }, [selectedStatus, searchQuery, allDepartments]);

  const departmentStatusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'maintenance', label: 'Maintenance' },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Department Management</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">Manage departments and teams</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <Button
            variant="outline"
            onClick={loadDepartmentData}
            disabled={loading}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={handleCreateDepartment}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Department
          </Button>
        </div>
      </div>

      {message && (
        <Alert className={message.type === 'error' ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'}>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className={message.type === 'error' ? 'text-red-800' : 'text-green-800'}>
            {message.text}
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('departments')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'departments'
                  ? 'border-[#006600] text-[#006600]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Building2 className="h-4 w-4 inline mr-2" />
              Departments
            </button>
            <button
              onClick={() => setActiveTab('hierarchy')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'hierarchy'
                  ? 'border-[#006600] text-[#006600]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FolderTree className="h-4 w-4 inline mr-2" />
              Hierarchy
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'settings'
                  ? 'border-[#006600] text-[#006600]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Settings className="h-4 w-4 inline mr-2" />
              Settings
            </button>
            <button
              onClick={() => setActiveTab('routing')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'routing'
                  ? 'border-[#006600] text-[#006600]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Route className="h-4 w-4 inline mr-2" />
              Routing Rules
            </button>
            <button
              onClick={() => setActiveTab('performance')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'performance'
                  ? 'border-[#006600] text-[#006600]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <BarChart3 className="h-4 w-4 inline mr-2" />
              Performance
            </button>
          </nav>
        </div>

        {activeTab === 'departments' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Departments List
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <Input
                      placeholder="Search departments..."
                      className="pl-10 w-64"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setCurrentPage(0); // Reset to first page when searching
                      }}
                    />
                    </div>
                    <StatusFilterDropdown
                      value={selectedStatus}
                      options={departmentStatusOptions}
                      placeholder="Filter by Status"
                      onChange={(value) => {
                        setSelectedStatus(value);
                        setCurrentPage(0); // Reset to first page when filter changes
                      }}
                      icon={Filter}
                    />
                  </div>
                </div>

                {(!safeDepartments || safeDepartments.length === 0) && (
                  <div className="text-center py-12">
                    <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 font-medium">No departments found</p>
                    <p className="text-sm text-gray-500 mt-1">
                      {selectedStatus || (searchQuery && searchQuery.trim() !== '')
                        ? 'Try adjusting your filters'
                        : 'No departments available'}
                    </p>
                    {(selectedStatus || (searchQuery && searchQuery.trim() !== '')) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedStatus(null);
                          setSearchQuery('');
                        }}
                        className="mt-4"
                      >
                        Clear Filters
                      </Button>
                    )}
                  </div>
                )}

                <div className="space-y-3">
                  {safeDepartments.map((department, index) => (
                    <div key={department?.id || `dept-${index}`} className="flex items-center justify-between p-5 border border-gray-200 rounded-lg hover:border-gray-300 hover:shadow-sm transition-all bg-white">
                      <div className="flex items-center gap-4 flex-1">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center shadow-sm">
                          {getDepartmentIcon(department?.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-gray-900 text-base">{department?.name || 'Unnamed Department'}</h3>
                            <Badge variant="outline" className={`${getDepartmentColor(department?.status || 'inactive')} text-xs font-medium`}>
                              {department?.status || 'inactive'}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600 mb-2 truncate">{department?.description || 'No description'}</p>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Users className="h-3.5 w-3.5" />
                              {department?.memberCount || 0} members
                            </span>
                            <span className="flex items-center gap-1">
                              <FileText className="h-3.5 w-3.5" />
                              {department?.ticketCount || 0} tickets
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-6 ml-4">
                        <div className="text-right border-r border-gray-200 pr-6">
                          <div className="text-sm font-semibold text-gray-900 mb-0.5">
                            {department?.manager?.name || department?.manager || 'Not assigned'}
                          </div>
                          <div className="text-xs text-gray-500 font-medium">
                            Manager
                          </div>
                          <div className="text-xs text-gray-500 mt-2">
                            {department?.createdAt ? new Date(department.createdAt).toLocaleDateString() : 'N/A'}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditDepartment(department)}
                            title="Edit Department"
                            className="h-9 px-3 border-amber-200 bg-white text-amber-600 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700 transition-all shadow-sm"
                          >
                            <Edit className="h-4 w-4 mr-1.5" />
                            <span className="text-xs font-medium">Edit</span>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewDepartment(department)}
                            title="View Details"
                            className="h-9 px-3 border-blue-200 bg-white text-blue-600 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-all shadow-sm"
                          >
                            <Eye className="h-4 w-4 mr-1.5" />
                            <span className="text-xs font-medium">View</span>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteDepartment(department.id);
                            }}
                            title="Delete Department"
                            className="h-9 px-3 border-red-200 bg-white text-red-600 hover:bg-red-50 hover:border-red-300 hover:text-red-700 transition-all shadow-sm"
                          >
                            <Trash2 className="h-4 w-4 mr-1.5" />
                            <span className="text-xs font-medium">Delete</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination Controls */}
                {safeDepartments.length > 0 && (
                  <div className="mt-6 pt-4 border-t border-gray-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="text-sm text-gray-600">
                      Showing <span className="font-medium text-gray-900">
                        {safeDepartments.length}
                      </span> of <span className="font-medium text-gray-900">
                        {totalElements}
                      </span> departments
                      {totalPages > 0 && (
                        <span className="ml-2 text-gray-500">
                          (Page {currentPage + 1} of {totalPages})
                        </span>
                      )}
                    </div>
                    
                    {totalPages > 1 && (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                          disabled={currentPage === 0 || loading}
                          className="flex items-center gap-1"
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Previous
                        </Button>
                        
                        <div className="flex items-center gap-1">
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i;
                            } else if (currentPage < 3) {
                              pageNum = i;
                            } else if (currentPage > totalPages - 4) {
                              pageNum = totalPages - 5 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }
                            
                            return (
                              <Button
                                key={pageNum}
                                variant={currentPage === pageNum ? "default" : "outline"}
                                size="sm"
                                onClick={() => setCurrentPage(pageNum)}
                                disabled={loading}
                                className={`min-w-[2.5rem] ${
                                  currentPage === pageNum 
                                    ? 'bg-blue-600 text-white hover:bg-blue-700' 
                                    : ''
                                }`}
                              >
                                {pageNum + 1}
                              </Button>
                            );
                          })}
                        </div>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                          disabled={currentPage >= totalPages - 1 || loading}
                          className="flex items-center gap-1"
                        >
                          Next
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                    
                    {totalPages === 1 && totalElements > 0 && (
                      <span className="text-sm text-gray-500">
                        All departments displayed
                      </span>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'hierarchy' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FolderTree className="h-5 w-5" />
                  Department Hierarchy
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label htmlFor="enableHierarchy">Enable Department Hierarchy</Label>
                      <p className="text-sm text-gray-600">Allow departments to have parent-child relationships</p>
                    </div>
                    <input
                      type="checkbox"
                      id="enableHierarchy"
                      checked={departmentHierarchy.enabled || false}
                      onChange={(e) => setDepartmentHierarchy({...departmentHierarchy, enabled: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                  
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Department Tree Structure</h3>
                    <div className="space-y-2">
                      {(departmentHierarchy.levels || []).map((level, index) => (
                        <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            {level.icon === 'crown' && <Crown className="h-4 w-4 text-yellow-600" />}
                            {level.icon === 'building' && <Building2 className="h-4 w-4 text-blue-600" />}
                            {level.icon === 'users' && <Users className="h-4 w-4 text-green-600" />}
                            {level.icon === 'cpu' && <Cpu className="h-4 w-4 text-purple-600" />}
                            <span className="font-medium text-gray-900">{level.name}</span>
                          </div>
                          <div className="flex items-center gap-2 ml-auto">
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              Level {level.level}
                            </Badge>
                            <span className="text-sm text-gray-500">{level.memberCount || 0} members</span>
                            <span className="text-sm text-gray-500">{level.subDepartments || 0} sub-departments</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Hierarchy Rules</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <Label htmlFor="inheritSettings">Inherit Settings</Label>
                          <p className="text-sm text-gray-600">Child departments inherit settings from parent departments</p>
                        </div>
                        <input
                          type="checkbox"
                          id="inheritSettings"
                          checked={departmentHierarchy.inheritSettings || false}
                          onChange={(e) => setDepartmentHierarchy({...departmentHierarchy, inheritSettings: e.target.checked})}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <Label htmlFor="overrideSettings">Allow Settings Override</Label>
                          <p className="text-sm text-gray-600">Child departments can override inherited settings</p>
                        </div>
                        <input
                          type="checkbox"
                          id="overrideSettings"
                          checked={departmentHierarchy.overrideSettings || false}
                          onChange={(e) => setDepartmentHierarchy({...departmentHierarchy, overrideSettings: e.target.checked})}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold">Hierarchy Settings</h3>
                      <p className="text-sm text-gray-600">Configure department hierarchy and inheritance rules</p>
                    </div>
                    <Button
                      onClick={handleSave}
                      disabled={saving}
                      className="flex items-center gap-2"
                    >
                      <Save className="h-4 w-4" />
                      {saving ? 'Saving...' : 'Save Hierarchy'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Department Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label htmlFor="enableDepartmentSettings">Enable Department-Specific Settings</Label>
                      <p className="text-sm text-gray-600">Allow each department to have custom settings</p>
                    </div>
                    <input
                      type="checkbox"
                      id="enableDepartmentSettings"
                      checked={departmentSettings.enabled || false}
                      onChange={(e) => setDepartmentSettings({...departmentSettings, enabled: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="defaultSLA">Default SLA (hours)</Label>
                      <Input
                        id="defaultSLA"
                        type="number"
                        value={departmentSettings.defaultSLA || 24}
                        onChange={(e) => setDepartmentSettings({...departmentSettings, defaultSLA: parseInt(e.target.value)})}
                        min="1"
                        max="168"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="maxTicketsPerAgent">Max Tickets per Agent</Label>
                      <Input
                        id="maxTicketsPerAgent"
                        type="number"
                        value={departmentSettings.maxTicketsPerAgent || 10}
                        onChange={(e) => setDepartmentSettings({...departmentSettings, maxTicketsPerAgent: parseInt(e.target.value)})}
                        min="1"
                        max="50"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="workingHours">Working Hours</Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="startTime">Start Time</Label>
                        <Input
                          id="startTime"
                          type="time"
                          value={departmentSettings.startTime || '09:00'}
                          onChange={(e) => setDepartmentSettings({...departmentSettings, startTime: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="endTime">End Time</Label>
                        <Input
                          id="endTime"
                          type="time"
                          value={departmentSettings.endTime || '17:00'}
                          onChange={(e) => setDepartmentSettings({...departmentSettings, endTime: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="workingDays">Working Days</Label>
                    <div className="grid grid-cols-7 gap-2">
                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                        <div key={day} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={day.toLowerCase()}
                            checked={departmentSettings.workingDays?.[day.toLowerCase()] || (day !== 'Sat' && day !== 'Sun')}
                            onChange={(e) => setDepartmentSettings({
                              ...departmentSettings,
                              workingDays: {
                                ...departmentSettings.workingDays,
                                [day.toLowerCase()]: e.target.checked
                              }
                            })}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <Label htmlFor={day.toLowerCase()} className="text-sm">{day}</Label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold">Department Settings</h3>
                      <p className="text-sm text-gray-600">Configure default settings for all departments</p>
                    </div>
                    <Button
                      onClick={handleSave}
                      disabled={saving}
                      className="flex items-center gap-2"
                    >
                      <Save className="h-4 w-4" />
                      {saving ? 'Saving...' : 'Save Settings'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'routing' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Route className="h-5 w-5" />
                  Department Routing Rules
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label htmlFor="enableAutoRouting">Enable Auto Routing</Label>
                      <p className="text-sm text-gray-600">Automatically route tickets to appropriate departments</p>
                    </div>
                    <input
                      type="checkbox"
                      id="enableAutoRouting"
                      checked={routingRules.enabled || false}
                      onChange={(e) => setRoutingRules({...routingRules, enabled: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                  
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Routing Rules</h3>
                    <div className="space-y-3">
                      {(routingRules.rules || []).map((rule, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                              <Route className="h-4 w-4 text-blue-600" />
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-900">{rule.name}</h4>
                              <p className="text-sm text-gray-600">{rule.description}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                  {rule.priority}
                                </Badge>
                                <span className="text-xs text-gray-500">{rule.condition}</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm">
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="sm">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Routing Configuration</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="routingStrategy">Routing Strategy</Label>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowRoutingStrategyModal(true)}
                          className="w-full justify-between h-10 text-left font-normal border-gray-300 bg-white"
                        >
                          <span className="text-sm text-gray-700 truncate">
                            {routingRules.strategy 
                              ? routingRules.strategy.split('-').map(word => 
                                  word.charAt(0).toUpperCase() + word.slice(1)
                                ).join(' ')
                              : 'Select routing strategy'}
                          </span>
                          <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0 ml-2" />
                        </Button>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="fallbackDepartment">Fallback Department</Label>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowFallbackDepartmentModal(true)}
                          className="w-full justify-between h-10 text-left font-normal border-gray-300 bg-white"
                        >
                          <span className="text-sm text-gray-700 truncate">
                            {routingRules.fallbackDepartment 
                              ? routingRules.fallbackDepartment.split('-').map(word => 
                                  word.charAt(0).toUpperCase() + word.slice(1)
                                ).join(' ')
                              : 'Select fallback department'}
                          </span>
                          <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0 ml-2" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold">Routing Rules</h3>
                      <p className="text-sm text-gray-600">Configure how tickets are routed to departments</p>
                    </div>
                    <Button
                      onClick={handleSave}
                      disabled={saving}
                      className="flex items-center gap-2"
                    >
                      <Save className="h-4 w-4" />
                      {saving ? 'Saving...' : 'Save Rules'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'performance' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Department Performance Metrics
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Building2 className="h-5 w-5 text-blue-600" />
                      <span className="text-sm font-medium text-blue-900">Total Departments</span>
                    </div>
                    <div className="text-xl sm:text-2xl font-bold text-blue-900">
                      {performanceMetrics.totalDepartments || 8}
                    </div>
                  </div>
                  
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="h-5 w-5 text-green-600" />
                      <span className="text-sm font-medium text-green-900">Total Members</span>
                    </div>
                    <div className="text-2xl font-bold text-green-900">
                      {performanceMetrics.totalMembers || 45}
                    </div>
                  </div>
                  
                  <div className="p-4 bg-yellow-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="h-5 w-5 text-yellow-600" />
                      <span className="text-sm font-medium text-yellow-900">Active Tickets</span>
                    </div>
                    <div className="text-2xl font-bold text-yellow-900">
                      {performanceMetrics.activeTickets || 127}
                    </div>
                  </div>
                  
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="h-5 w-5 text-purple-600" />
                      <span className="text-sm font-medium text-purple-900">Avg Resolution Time</span>
                    </div>
                    <div className="text-2xl font-bold text-purple-900">
                      {performanceMetrics.avgResolutionTime || '4.2h'}
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h3 className="text-lg font-semibold">Top Performing Departments</h3>
                  {safeDepartments.slice(0, 5).map((department, index) => (
                    <div key={department.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-bold text-blue-600">#{index + 1}</span>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{department.name}</h4>
                          <p className="text-sm text-gray-600">{department.type}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="text-lg font-bold text-green-600">
                            {department.performanceScore || 85}%
                          </div>
                          <div className="text-xs text-gray-500">Performance Score</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium text-gray-900">
                            {department.ticketsResolved || 0}
                          </div>
                          <div className="text-xs text-gray-500">Tickets Resolved</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Modals */}
      <RoutingStrategyModal
        isOpen={showRoutingStrategyModal}
        onClose={() => setShowRoutingStrategyModal(false)}
        onSelect={handleRoutingStrategySelect}
        selectedValue={routingRules.strategy}
      />

      <FallbackDepartmentModal
        isOpen={showFallbackDepartmentModal}
        onClose={() => setShowFallbackDepartmentModal(false)}
        onSelect={handleFallbackDepartmentSelect}
        selectedValue={routingRules.fallbackDepartment}
        departments={allDepartments}
      />

      <AddEditDepartmentModal
        isOpen={showAddDepartmentModal}
        onClose={() => {
          setShowAddDepartmentModal(false);
          setEditingDepartment(null);
        }}
        department={editingDepartment}
        onSave={handleSaveDepartment}
        saving={saving}
      />

      <ViewDepartmentModal
        isOpen={showViewDepartmentModal}
        onClose={() => {
          setShowViewDepartmentModal(false);
          setSelectedDepartment(null);
        }}
        department={selectedDepartment}
        onEdit={(department) => {
          setShowViewDepartmentModal(false);
          setEditingDepartment(department);
          setShowAddDepartmentModal(true);
        }}
      />

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setDepartmentToDelete(null);
        }}
        onConfirm={confirmDeleteDepartment}
        title="Delete Department"
        message={
          departmentToDelete 
            ? `Are you sure you want to delete "${departmentToDelete.name}"? This action cannot be undone and all associated data will be permanently removed.`
            : 'Are you sure you want to delete this department?'
        }
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default DepartmentManagement;